/**
 * BrandLoader — the screen-level loader of AxiomGO (T1, SPRINT-UI-REFOUNDATION-1).
 *
 * The brand mark (inline SVG, `currentColor`, monochrome by house rule —
 * see `AxiomLogo`) pulses in opacity only: 2.2 s ease-in-out between 0.35
 * and 1. No scale, no glow, never an `<img>` — a raster logo blurs on the
 * shop-floor tablet, and `<img>` cannot follow the theme colour.
 *
 * Scope: this replaces the grey skeleton at boot (`AuthGuard`) and on lazy
 * routes (`Router`). The per-turn "thinking" indicator inside the chat is a
 * different thing (prompt-kit `Loader`, task B4) and must not use this.
 *
 * Reduced motion: `motion-reduce:animate-none` stops the pulse and
 * `motion-reduce:opacity-70` keeps the mark legible (R9).
 */
import { useTranslation } from 'react-i18next'
import { AxiomLogo } from '@/components/icons/AxiomLogo'
import { cn } from '@/lib/utils'

type BrandLoaderSize = 32 | 48 | 64

type BrandLoaderProps = {
  /** Mark size in px. 48 = full screen, 32 = inside a panel, 64 = hero. */
  size?: BrandLoaderSize
  /** Extra classes for the wrapper (e.g. `h-screen bg-axg-bg`). */
  className?: string
  /** i18n key read by screen readers. Defaults to `common.loading`. */
  labelKey?: string
}

export function BrandLoader({
  size = 48,
  className,
  labelKey = 'common.loading',
}: BrandLoaderProps) {
  const { t } = useTranslation()
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn('flex items-center justify-center text-axg-primary', className)}
      data-testid="brand-loader"
    >
      <AxiomLogo
        size={size}
        className="animate-brand-pulse motion-reduce:animate-none motion-reduce:opacity-70"
      />
      <span className="sr-only">{t(labelKey)}</span>
    </div>
  )
}
